import { useContext, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LeftMenu from "./menus/LeftMenu";
import HeaderBar from "./headers/HeaderBar";
import { LanguageContext } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useIsPremium } from "../hooks/useIsPremium";
import { getFunctions, httpsCallable } from "firebase/functions";
import StarIcon from "@mui/icons-material/Star";

export default function ProfileUserDesktop() {
  const theme = useTheme();
  const { translate, language } = useContext(LanguageContext);
  const { currentUser } = useAuth();
  const { isPremium, loading: premiumLoading } = useIsPremium();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem("darkMode");
      if (saved !== null) return JSON.parse(saved);
    } catch {}
    return false;
  });
  const [portalUrl, setPortalUrl] = useState("");
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const syncDarkPreference = (value) => {
    setDarkMode(value);
    try {
      localStorage.setItem("darkMode", JSON.stringify(value));
      window.dispatchEvent(new CustomEvent("darkmode:changed", { detail: value }));
    } catch {}
  };

  useEffect(() => {
    if (!currentUser) return;
    (async () => {
      const ref = doc(db, "profilsUsers", currentUser.uid);
      const snap = await getDoc(ref);
      const data = snap.exists() ? snap.data() : {};
      setForm({
        name: data.name || currentUser.displayName || "",
        email: data.email || currentUser.email || "",
        phone: data.phone || "",
      });
      if (typeof data.darkMode === "boolean") {
        syncDarkPreference(data.darkMode);
      }
      setLoading(false);
    })();
  }, [currentUser, language]);

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));
  const handleSave = async () => {
    if (!currentUser) return;
    setSaving(true);
    await setDoc(
      doc(db, "profilsUsers", currentUser.uid),
      {
        name: form.name,
        email: form.email,
        phone: form.phone,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    setSaving(false);
  };

  useEffect(() => {
    let cancelled = false;
    if (!isPremium) {
      setPortalUrl("");
      setPortalError("");
      setPortalLoading(false);
      return;
    }
    const functions = getFunctions();
    const createPortalLink = httpsCallable(
      functions,
      "ext-firestore-stripe-payments-createPortalLink"
    );
    setPortalLoading(true);
    setPortalError("");
    createPortalLink({
      returnUrl: `${window.location.origin}/app/profile-user`,
    })
      .then(({ data }) => {
        if (!cancelled) {
          setPortalUrl(data?.url || "");
        }
      })
      .catch((err) => {
        console.error("Failed to create billing portal link:", err);
        if (!cancelled) {
          setPortalError(
            translate("ManageSubscriptionError") ||
              "Unable to load the billing portal link. Please try again."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPortalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isPremium, translate]);

  const handleOpenPortal = () => {
    if (!portalUrl || portalLoading) return;
    window.location.assign(portalUrl);
  };

  const handleCancelSubscription = () => {
    if (!portalUrl || portalLoading) return;
    setCancelDialogOpen(true);
  };

  const confirmCancelSubscription = () => {
    if (!portalUrl || portalLoading) return;
    setCancelDialogOpen(false);
    window.location.assign(portalUrl);
  };

  const handleDarkModeToggle = async (value) => {
    syncDarkPreference(value);
    if (!currentUser) return;
    try {
      await setDoc(
        doc(db, "profilsUsers", currentUser.uid),
        { darkMode: value },
        { merge: true }
      );
    } catch (err) {
      console.error("Failed to save dark mode preference", err);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        bgcolor: "background.default",
        color: "text.primary",
        minHeight: "100vh",
      }}
    >
      <LeftMenu />
      <Box sx={{ flex: 1, p: 3 }}>
        <HeaderBar
          title={translate("UserProfile") || "User Profile"}
          subtitle={translate("YourUserProfil") || "Your user profile"}
        />
        <Stack spacing={3} sx={{ mt: 3, maxWidth: 960, mx: "auto" }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              border: `1px solid ${
                theme.palette.mode === "dark"
                  ? "#1f2937"
                  : "rgba(15,23,42,0.08)"
              }`,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(9,9,11,0.9), rgba(37,37,46,0.95))"
                  : "linear-gradient(135deg, #eef2ff, #e0f7ff)",
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography variant="overline" sx={{ letterSpacing: 1 }}>
                  {translate("UserProfile") || "User profile"}
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {form.name || translate("WelcomeToTalkmate") || "Welcome"}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {form.email}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ mt: 2 }}
                  flexWrap="wrap"
                >
                  <Chip
                    icon={isPremium ? <StarIcon /> : undefined}
                    label={
                      isPremium
                        ? translate("YouArePremium") || "You are Premium"
                        : translate("FreePlanTitle") || "Free plan"
                    }
                    color={isPremium ? "warning" : "default"}
                    sx={{
                      color: isPremium ? "black" : "text.primary",
                      fontWeight: 600,
                      bgcolor: isPremium ? "gold" : "transparent",
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={darkMode}
                        onChange={(e) => handleDarkModeToggle(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={darkMode ? translate("DarkModeOn") : translate("DarkModeOff")}
                    sx={{ mt: 0.5 }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                {!premiumLoading && isPremium && (
                  <Stack spacing={1.5}>
                    <Typography variant="body2" color="text.secondary">
                      {translate("ManageSubscriptionHint") ||
                        "Update your billing information or cancel from the Stripe portal."}
                    </Typography>
                    <div
                      style={{
                        width: "auto",

                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenPortal}
                        disabled={portalLoading || !portalUrl}
                        sx={{ textTransform: "none", minWidth: 220 }}
                      >
                        {portalLoading
                          ? translate("Loading") || "Loading…"
                          : translate("ManageSubscription") ||
                            "Manage subscription"}
                      </Button>
                    </div>

                    <div
                      style={{
                        width: "auto",

                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancelSubscription}
                        disabled={portalLoading || !portalUrl}
                        sx={{ textTransform: "none", minWidth: 220 }}
                      >
                        {translate("CancelSubscription") ||
                          "Cancel subscription"}
                      </Button>
                    </div>
                    {portalError && (
                      <Typography variant="caption" color="error">
                        {portalError}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {translate("EditProfile") || "Profile details"}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={translate("Name") || "Name"}
                  value={form.name}
                  onChange={handleChange("name")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={translate("EmailAddress") || "Email"}
                  value={form.email}
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={translate("Phone") || "Phone"}
                  value={form.phone}
                  onChange={handleChange("phone")}
                />
              </Grid>
            </Grid>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ mt: 3 }}
            >
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || loading}
                sx={{ px: 4, textTransform: "none" }}
              >
                {translate("Save") || "Save"}
              </Button>
            </Stack>
          </Paper>
        </Stack>
      </Box>
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {translate("CancelSubscription") || "Cancel subscription"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            {translate("ConfirmCancelSubscription") ||
              "Are you sure you want to cancel this subscription at the period end?"}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            disabled={portalLoading}
          >
            {translate("Cancel") || "Cancel"}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmCancelSubscription}
            disabled={portalLoading}
          >
            {translate("CancelSubscription") || "Cancel subscription"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
